import { useAlert } from 'react-alert';
import { useDispatch } from 'react-redux';
import I18n from 'i18n-js';
import { bool, func, shape, string } from 'prop-types';

import { claimInterval } from '@lootboy/lib/actions/IntervalProgressActions';
import { useTimer } from '@lootboy/lib/hooks';
import { MINUTE_MS } from '@lootboy/lib/time';
import { getEndOfUTCDay } from '@lootboy/lib/utils/dateAndTime';

import Alert from '~/components/Alert';
import BottomPopupButton, { SIDES } from '~/components/BottomPopupButton';
import CaptchaView from '~/components/CaptchaView';
import DailyReminderPopup from '~/components/DailyTasksView/DailyTiles/Tile/DailyReminderPopup';
import {
  PopupMinimalisticWithTransition,
  TransitionsClassNames,
} from '~/components/PopupsWithTransition';

import useBooleanState from '~/hooks/useBooleanState';

import DailyRewardCountdownText from './DailyRewardCountdownText';

import style from './Footer.module.scss';

export default function Footer({
  lastClaimAt,
  dailyNotificationsEnabled,
  siteNotificationsEnabled,
  popupGradient,
  reward,
  receivedReward,
  setReceivedReward,
  ...props
}) {
  const { isCaptchaRequired, active, next } = reward || {};

  const alert = useAlert();
  const dispatch = useDispatch();
  const [showNotificationsPopup, toggleShowNotificationsPopup] =
    useBooleanState();
  const { hours, minutes } = useTimer(getEndOfUTCDay(), {
    interval: MINUTE_MS,
  });

  const [captcha, toggleCaptcha] = useBooleanState();

  const claimReward = async (token) => {
    try {
      await dispatch(claimInterval(token));
      setReceivedReward(true);
    } catch ({ message }) {
      alert.error(
        <Alert
          body={message}
          title={I18n.t('common_error')}
        />,
      );
    } finally {
      if (
        !lastClaimAt &&
        !dailyNotificationsEnabled &&
        siteNotificationsEnabled
      ) {
        toggleShowNotificationsPopup();
      }
    }
  };

  const claimRewardWrapper = () => claimReward();

  const onCaptchaChanged = (token) => {
    toggleCaptcha();
    return claimReward(token);
  };

  if (!(active || next)) {
    return null;
  }

  return (
    <div className={style.footer}>
      {(hours !== 0 || minutes !== 0) && next && (
        <div className={style.footerText}>
          <DailyRewardCountdownText
            hours={hours}
            minutes={minutes}
          />
        </div>
      )}
      <BottomPopupButton
        side={SIDES.RIGHT}
        disabled={next}
        divider
        onClick={isCaptchaRequired ? toggleCaptcha : claimRewardWrapper}
      >
        {I18n.t('DailyView_popup_button_claim')}
      </BottomPopupButton>
      <PopupMinimalisticWithTransition
        {...props}
        show={captcha}
        onClose={toggleCaptcha}
        style={{ background: `linear-gradient(${popupGradient})` }}
        token
        transitionClassName={TransitionsClassNames.THIRD_MODAL}
      >
        <CaptchaView onChange={onCaptchaChanged} />
      </PopupMinimalisticWithTransition>
      <DailyReminderPopup
        popupGradient={popupGradient}
        show={showNotificationsPopup}
        toggleShow={toggleShowNotificationsPopup}
      />
    </div>
  );
}

Footer.propTypes = {
  lastClaimAt: string,
  dailyNotificationsEnabled: bool.isRequired,
  siteNotificationsEnabled: bool.isRequired,
  popupGradient: string,
  receivedReward: bool.isRequired,
  reward: shape({
    image: string,
    src: string,
    title: string,
    active: bool,
    next: bool,
    collected: bool,
  }),
  setReceivedReward: func.isRequired,
};
